import * as React from 'react';
import type { FieldPath, FieldValues, Path, PathValue, UseControllerProps, UseFormGetValues } from 'react-hook-form';

import type { Props as PlainDatePickerProps } from 'af-components/Controls/DateInput';

import DisplayCell from '../../RHFTableCellComponents/DatePicker/DisplayCell';
import type { CellError } from '../../RHFTableCellComponents/error.types';
import RHFUnregisteredFieldWrapper from '../RHFUnregisteredFieldWrapper';

type Props<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
> = {
	rhfId?: number | string;
	getValues: UseFormGetValues<TFieldValues>;
	readonly?: boolean;
	bold?: boolean;
} & UseControllerProps<TFieldValues, TName, TTransformedValues> & PlainDatePickerProps;

function DatePicker<
	TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TTransformedValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, readonly, ...rest } = props;

	const renderDatePicker = React.useCallback((
		{ value, error }: {
			value: PathValue<TFieldValues, keyof TFieldValues & Path<TFieldValues>>;
			error: Nullable<CellError>;
		}) => (
		<DisplayCell
			{...rest}
			elementKey={rhfId}
			error={error ?? undefined}
			isWhite={!readonly}
			key={name + rhfId}
			placeholder={placeholder}
			readonly={readonly}
			value={value}
		/>
	),
		[rhfId, name, placeholder, rest, readonly]
	);

	return (
		<RHFUnregisteredFieldWrapper
			{...rest}
			name={name}
			render={renderDatePicker}
		/>
	);
}

export default React.memo(DatePicker) as typeof DatePicker;
